import { memo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Heart, Truck, ArrowDown } from 'lucide-react';
import { VEHICLE_PLACEHOLDER_URL } from '../../constants/vehiclePlaceholder';
import { useDispatch, useSelector } from 'react-redux';
import { DEFAULT_CARFAX_BADGE_IMAGE_URL, pickPublicCarfaxHref } from '../../constants/carfaxPublic';
import { useLocale } from '../../contexts/LocaleContext';
import { toggleWishlist } from '../../features/wishlist/wishlistSlice';
import { addToWishlist as addToWishlistAPI, removeFromWishlist as removeFromWishlistAPI } from '../../services/wishlistService';
import GoodDealBadge from './GoodDealBadge';
import '../AllCss/CarCard.css';

function formatCurrency(value) {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(Number(value));
}

function formatMiles(value) {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return `${Number(value).toLocaleString()} mi`;
}

function CarCard({ car, isUpcoming = false, onCompareChange, isCompared = false, maxCompareReached = false, marketInsight = null }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLocale();
  const wishlistIds = useSelector((state) => state.wishlist.items);
  // Normalize IDs to strings for comparison
  const carIdString = car ? String(car.id) : null;
  const isWishlisted = carIdString ? wishlistIds.some(id => String(id) === carIdString) : false;
  const { isAuthenticated } = useSelector((state) => state.auth);

  // Split title into two lines: year + make (line 1), model + trim (line 2)
  const titleLine1 = `${car.year ?? ''} ${car.make ?? ''}`.trim();
  const titleLine2 = `${car.model ?? ''}${car.trim ? ` ${car.trim}` : ''}`.trim();
  const fullTitle = `${titleLine1} ${titleLine2}`.trim();
  
  // Get the first image from the images array, or use a placeholder
  const imageUrl = (car.images && car.images.length > 0) 
    ? car.images[0] 
    : VEHICLE_PLACEHOLDER_URL;

  const handleWishlistClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isAuthenticated) {
      // Store the car ID they want to like in localStorage
      localStorage.setItem('pendingWishlistCarId', String(car.id));
      // Redirect to auth page with current location as redirect (includes pathname and search params)
      const redirectUrl = location.pathname + location.search;
      navigate(`/auth?redirect=${encodeURIComponent(redirectUrl)}`);
      return;
    }

    try {
      if (isWishlisted) {
        // Remove from wishlist
        await removeFromWishlistAPI(car.id);
        dispatch(toggleWishlist(car.id));
      } else {
        // Add to wishlist
        await addToWishlistAPI(car.id);
        dispatch(toggleWishlist(car.id));
      }
    } catch (error) {
      console.error('Error toggling wishlist:', error);
      // Still update Redux for optimistic UI, but show error
      dispatch(toggleWishlist(car.id));
    }
  };

  // Calculate estimated monthly payment (simple calculation: price/72 months at ~5% APR)
  const calculateMonthlyPayment = (price) => {
    const monthlyRate = 0.05 / 12; // 5% annual rate
    const numPayments = 72; // 6 years
    const principal = parseFloat(price) || 0;
    const monthlyPayment = (principal * monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / 
                          (Math.pow(1 + monthlyRate, numPayments) - 1);
    return Math.round(monthlyPayment);
  };

  const monthlyPayment = calculateMonthlyPayment(car.price);

  const carfaxHref = pickPublicCarfaxHref(car);
  const carfaxBadgeSrc = DEFAULT_CARFAX_BADGE_IMAGE_URL;

  return (
    <Link
      to={`/cars/${car.stockNumber || car.id}`}
      className="CarCard"
      draggable={false}
      onDragStart={(e) => e.preventDefault()}
    >
        <div className="CarCard-image-container">
          <img
            src={imageUrl}
            alt={fullTitle || t('carCard.vehicle')}
            className="CarCard-image"
            draggable={false}
            loading="lazy"
            decoding="async"
            data-loaded="false"
            onLoad={(e) => { e.currentTarget.dataset.loaded = 'true'; }}
          />
          {car.expectedLaunchDate && (
            <div className="expected-launch-badge-card">
              <span>{t('carCard.expectedLaunch')} {car.expectedLaunchDate}</span>
            </div>
          )}
          {car.isPriceDrop && car.originalPrice && car.price < car.originalPrice && (
            <div className="price-drop-badge">{t('carCard.priceDrop')}</div>
          )}
          {!isUpcoming && <GoodDealBadge insight={marketInsight} variant="card" />}
          <button 
            className={`CarCard-wishlist ${isWishlisted ? 'active' : ''}`}
            onClick={handleWishlistClick}
            aria-label={t('carCard.addToWishlist')}
          >
            <Heart size={20} fill={isWishlisted ? 'currentColor' : 'none'} />
          </button>
        </div>
      
      <div className="CarCard-body">
        <h3 className="CarCard-title">
          <span className="CarCard-title-line1">{titleLine1 || t('carCard.vehicle')}</span>
          {titleLine2 && <span className="CarCard-title-line2">{titleLine2}</span>}
        </h3>
        
        <div className="CarCard-details">
          <div className="CarCard-meta">
            <span className="CarCard-condition">
              {car.condition === 'certified' ? 'CERTIFIED' : car.condition ? car.condition.toUpperCase() : 'USED'}
            </span>
            <span className="CarCard-separator">•</span>
            <span>{formatMiles(car.mileage)}</span>
          </div>
        </div>

        {isUpcoming ? (
          <div className="CarCard-upcoming-info">
            <div className="CarCard-price-section">
              {car.price ? (
                <div className="CarCard-price-estimated">
                  <span className="estimated-label">{t('carCard.estimatedPrice')}</span>
                  <div className="CarCard-price">{formatCurrency(car.price)}</div>
                </div>
              ) : (
                <div className="CarCard-price-tbd">
                  <span>{t('carCard.priceTbd')}</span>
                </div>
              )}
            </div>
            <div className="CarCard-status-info">
              <span className="upcoming-status-badge">{car.status ? car.status.replace('-', ' ').toUpperCase() : 'COMING SOON'}</span>
            </div>
          </div>
        ) : (
          <>
        <div className="CarCard-price-section">
          <div className="CarCard-price-container">
            <div className="CarCard-price">{formatCurrency(car.price)}</div>
            {car.isPriceDrop && car.originalPrice && car.price < car.originalPrice && (
              <div className="CarCard-original-price">
                    <div className="price-drop-badge-inline">
                      <ArrowDown size={12} />
                    </div>
                    <span className="original-price-text">{t('carCard.was')} {formatCurrency(car.originalPrice)}</span>
              </div>
            )}
          </div>
          <div className="CarCard-payment">
            {t('carCard.estimatedMo')} ${monthlyPayment}/mo
          </div>
          <div className="CarCard-payment-details">
            {t('carCard.cashDown')}
          </div>
        </div>

        <div className="CarCard-shipping">
          <div className="shipping-line-1">
            <Truck size={16} className="shipping-icon" />
            <span className="shipping-cost">{t('carCard.deliveryQuote')}</span>
          </div>
        </div>

        {!isUpcoming && (carfaxHref || onCompareChange) && (
          <div className="CarCard-post-actions" onMouseDown={(e) => e.stopPropagation()}>
            {carfaxHref ? (
              <div
                className="CarCard-carfax"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  className="CarCard-carfax-btn"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    window.open(carfaxHref, '_blank', 'noopener,noreferrer');
                  }}
                  aria-label="View CARFAX Vehicle History Report (opens in new tab)"
                  title="Opens in new tab"
                >
                  <img
                    src={carfaxBadgeSrc}
                    alt=""
                    className="CarCard-carfax-img"
                    loading="lazy"
                    decoding="async"
                  />
                </button>
              </div>
            ) : (
              <span className="CarCard-post-actions-spacer" aria-hidden />
            )}

            {onCompareChange ? (
              <div className="CarCard-compare">
                <label className="CarCard-compare-checkbox" onMouseDown={(e) => e.preventDefault()}>
                  <input
                    type="checkbox"
                    checked={isCompared}
                    onChange={(e) => {
                      e.stopPropagation();
                      onCompareChange(car.id, e.target.checked);
                    }}
                    disabled={!isCompared && maxCompareReached}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <span className="CarCard-compare-text">{t('carCard.compare')}</span>
                </label>
              </div>
            ) : null}
          </div>
        )}
          </>
        )}
      </div>
    </Link>
  );
}

export default memo(CarCard);


